import React from 'react'
import { useSelector } from 'react-redux'
import pluralize from 'pluralize'
import { StyleService, useStyleSheet } from '@src/style/service'
import { InlineAlert } from '@components/InlineAlert'
import { userSelector } from '@src/selectors/app'
import { EhrAppointmentAttendanceChargeKind, UiInlineAlert } from '@src/types'
import { insurancePolicySelector } from '../../models/nutritionistHub.selectors'

interface InsuranceAuthorizationDetailsProps {
  chargeKind: EhrAppointmentAttendanceChargeKind | null | undefined
}

export const InsuranceAuthorizationDetails = ({
  chargeKind,
}: InsuranceAuthorizationDetailsProps) => {
  const styles = useStyleSheet(themedStyles)
  const user = useSelector(userSelector)
  const primaryCoachingKind = user?.primaryCoachingKind
  const showInsuranceUi = primaryCoachingKind?.insuranceReimbursable === true
  const insurancePolicy = useSelector(insurancePolicySelector)
  const callsOccurred = insurancePolicy?.unitsOccurred ?? 0
  const callsAuthorized = insurancePolicy?.unitsAuthorized ?? undefined

  if (
    !showInsuranceUi ||
    !insurancePolicy ||
    insurancePolicy.declined ||
    callsAuthorized === undefined ||
    callsAuthorized <= 0
  ) {
    return null
  }

  const callsRemaining = Math.max(callsAuthorized - callsOccurred, 0)

  if (chargeKind === EhrAppointmentAttendanceChargeKind.BsInsuranceCovered) {
    // Expected to be insurance covered, has limited authorized units
    const callsRemainingMessage = `You have ${callsRemaining} remaining this year.`
    const callNumberMessage =
      callsOccurred > 0
        ? `You've had ${callsOccurred} insurance-covered video ${pluralize('call', callsOccurred)}.`
        : "You haven't had any insurance-covered video calls."

    return (
      <InlineAlert
        category={UiInlineAlert.Info}
        message={`${callNumberMessage} ${callsRemainingMessage}`}
        style={styles.container}
      />
    )
  }

  if (chargeKind === EhrAppointmentAttendanceChargeKind.BsSelfPay) {
    // Insurance is valid, but we expect the user will be out of units at this point
    const message =
      callsRemaining > 0
        ? `You have ${pluralize(
            'insurance-covered call',
            callsRemaining,
            true,
          )} remaining which will be applied to your next upcoming ${pluralize(
            'call',
            callsRemaining,
          )}.`
        : `You have used all of your ${pluralize('insurance-covered call', callsAuthorized, true)}.`

    return (
      <InlineAlert
        category={UiInlineAlert.Warning}
        message={`${message} This call will not be covered.`}
        style={styles.container}
      />
    )
  }

  return null
}

const themedStyles = StyleService.create({
  container: {
    marginBottom: 16,
  },
})
